import { CSSProperties, useMemo } from 'react';
import { BigNum, QUOTE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { POOL_NAMES_BY_POOL_ID } from 'src/constants/constants';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import { getCleanedMarketSymbol } from 'src/utils/market';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';
import { InsuranceFundListData } from './types';
import ActionButtons from './ActionButtons';
import NumLib from 'src/utils/NumLib';
import Env from 'src/environmentVariables/EnvironmentVariables';

interface TableBodyRowProps extends WithClassName {
	row: InsuranceFundListData;
	isPortfolioPage?: boolean;
	style: CSSProperties;
}

const TableBodyRow = ({ row, isPortfolioPage, style }: TableBodyRowProps) => {
	const spotMarkets = useSpotMarkets();
	const spotMarket = spotMarkets[row.marketIndex];
	const showAccountValues = useShowAccountValues();
	const setDrawerStore = useDrawersStore((s) => s.set);

	if (!spotMarket) {
		return null;
	}

	const marketSymbol = spotMarket.symbol;
	const cleanedMarketSymbol = getCleanedMarketSymbol(marketSymbol);
	const isUsdc = marketSymbol === 'USDC';
	const poolName = POOL_NAMES_BY_POOL_ID[row.poolId];

	const vaultBalanceDisplay = NumLib.millify(row.vaultBalance);

	const apyPercent = row.apy || 0;
	const apyToShow =
		apyPercent > 0 && apyPercent < 0.01
			? '< 0.01'
			: !isFinite(apyPercent) || apyPercent > 10000
				? '> 10,000'
				: apyPercent.toFixed(2);

	const handleViewPerformance = (event: React.MouseEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();

		setDrawerStore((s) => {
			s.insuranceFundVault.show = true;
			s.insuranceFundVault.marketIndex = row.marketIndex;
		});
	};

	const decimals = isUsdc ? 2 : spotMarket.precisionExp.toNumber();

	return (
		<div
			className="grid h-[68px] cursor-pointer grid-cols-subgrid px-md py-lg hover:bg-button-disabled"
			style={style}
			onClick={handleViewPerformance}
		>
			<div className="flex items-center gap-x-md">
				<MarketIcon marketSymbol={cleanedMarketSymbol} sizeClass="size-2xl" />
				<div className="flex flex-col gap-y-xs">
					<span className="typo-label-sm text-text-default">
						{cleanedMarketSymbol}
					</span>
					{poolName !== 'Main Market' && (
						<span className="typo-body-xs text-text-tertiary">{poolName}</span>
					)}
				</div>
			</div>

			{!isPortfolioPage && (
				<>
					<div className="flex items-center gap-x-md typo-label-sm text-text-default">
						<MarketIcon marketSymbol={cleanedMarketSymbol} sizeClass="size-4" />
						{vaultBalanceDisplay.displayString}
					</div>

					<div
						className={cj(
							'flex items-center typo-label-sm',
							apyPercent < 0
								? 'text-text-negative-red-button'
								: 'text-text-positive-green-button'
						)}
					>
						{apyToShow}%
					</div>
				</>
			)}

			<div className="flex flex-col gap-y-xs">
				<div className="flex items-center gap-x-xs typo-label-sm text-text-default">
					{(!showAccountValues || row.yourBalanceBase !== 0) && (
						<MarketIcon marketSymbol={cleanedMarketSymbol} sizeClass="size-4" />
					)}
					<div className="text-text-default">
						{!showAccountValues
							? '*****'
							: row.yourBalanceBase === 0
								? '-'
								: row.yourBalanceBase.toLocaleString(Env.locale, {
										minimumFractionDigits: decimals,
										maximumFractionDigits: decimals,
									})}
					</div>
				</div>
				{row.yourBalanceBase !== 0 && (
					<span className="typo-body-xs text-text-secondary">
						{showAccountValues
							? BigNum.fromPrint(
									`${row.yourBalanceQuote}`,
									QUOTE_PRECISION_EXP
								).toNotional()
							: '*****'}
					</span>
				)}
			</div>

			<div className="flex flex-col gap-y-xs">
				<div className="flex items-center gap-x-xs typo-label-sm text-text-default">
					{(!showAccountValues || row.cumulativeEarningsBase !== 0) && (
						<MarketIcon marketSymbol={cleanedMarketSymbol} sizeClass="size-4" />
					)}
					<div className="text-text-default">
						{!showAccountValues
							? '*****'
							: row.cumulativeEarningsBase === 0
								? '-'
								: row.cumulativeEarningsBase.toLocaleString(Env.locale, {
										minimumFractionDigits: decimals,
										maximumFractionDigits: decimals,
									})}
					</div>
				</div>
				{row.cumulativeEarningsBase !== 0 && (
					<span className="typo-body-xs text-text-secondary">
						{showAccountValues
							? BigNum.fromPrint(
									`${row.cumulativeEarningsQuote}`,
									QUOTE_PRECISION_EXP
								).toNotional()
							: '*****'}
					</span>
				)}
			</div>

			<ActionButtons row={row} isPortfolioPage={isPortfolioPage} />
		</div>
	);
};

interface TableBodyProps extends WithClassName {
	rowData: InsuranceFundListData[];
	isPortfolioPage?: boolean;
}

const TableBody = ({ className, rowData, isPortfolioPage }: TableBodyProps) => {
	const style = useMemo(() => {
		const numCols = isPortfolioPage ? 4 : 6;

		return { gridColumn: `span ${numCols} / span ${numCols}` };
	}, [isPortfolioPage]);

	return (
		<div className={cj('grid grid-cols-subgrid', className)} style={style}>
			{rowData.map((row) => (
				<TableBodyRow
					key={`${row.marketIndex} ${row.poolId}`}
					row={row}
					isPortfolioPage={isPortfolioPage}
					style={style}
				/>
			))}
		</div>
	);
};

export { TableBody };
